<template>
  <div v-if="pills.length > 0" class="custom-field-value-pills">
    <span
      v-for="pill in pills"
      :key="pill.field.id"
      :title="`${pill.field.name}: ${pill.text}`"
      class="custom-field-value-pills__pill"
    >
      <template v-if="pill.option">
        <span class="custom-field-value-pills__label">{{ pill.field.name }}</span>
        <span :class="`custom-field-value-pills__swatch ${pill.option.color}`" />
        <span class="custom-field-value-pills__text">{{ pill.option.name }}</span>
      </template>
      <template v-else-if="pill.isCheckbox">
        <i class="ddi ddi-check" />
        <span class="custom-field-value-pills__text">{{ pill.field.name }}</span>
      </template>
      <template v-else>
        <span class="custom-field-value-pills__label">{{ pill.field.name }}</span>
        <span class="custom-field-value-pills__text">{{ pill.text }}</span>
      </template>
    </span>
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Helpers */
import CustomFieldAssistant, {
  ECustomFieldTypes,
} from '@/helpers/custom-field-assistant'

/* Data types */
import {
  ICustomField,
  ICustomFieldOption,
  ICustomFieldValue,
} from '@/api/types/response.types'

interface IPill {
  field: ICustomField
  text: string
  option: ICustomFieldOption | null
  isCheckbox: boolean
}

/**
 * Read-only summary of an item's custom field values, used on listings and kanban cards.
 * Only fields in `fields` are shown, in that order, and only when the item has a value for them.
 * Unchecked checkboxes are skipped since they carry no information at a glance.
 */
export default defineComponent({
  name: 'CustomFieldValuePills',

  props: {
    fields: {
      type: Array as () => ICustomField[],
      default: (): ICustomField[] => [],
    },
    values: {
      type: Array as () => ICustomFieldValue[],
      default: (): ICustomFieldValue[] => [],
    },
  },

  computed: {
    pills(): IPill[] {
      const result: IPill[] = []

      this.fields.forEach(field => {
        const value =
          (this.values || []).find(v => v.customFieldID === field.id) || null

        if (!CustomFieldAssistant.hasValue(field, value)) {
          return
        }

        const isCheckbox = field.customFieldType === ECustomFieldTypes.Checkbox
        if (isCheckbox && !value.checkboxData) {
          return
        }

        result.push({
          field,
          text: CustomFieldAssistant.displayText(field, value),
          option: CustomFieldAssistant.optionFor(field, value),
          isCheckbox,
        })
      })

      return result
    },
  },
})
</script>

<style lang="scss">
.custom-field-value-pills {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  max-width: 100%;

  &__pill {
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    padding: 2px 8px;
    margin: 0 4px 2px 0;
    font-size: 12px;
    line-height: 1.4;
    white-space: normal;
    background: var(--color-background-secondary);
    color: var(--color-text-secondary);
    border: solid 1px var(--color-text-third);
    border-radius: 20px;

    &:last-child {
      margin-right: 0;
    }

    .ddi-check {
      margin-right: 4px;
      font-size: 10px;
      color: $color--green;
    }
  }

  /* Dropdown option color, shown as a dot so the option text matches the rest of the pill */
  &__swatch {
    display: inline-block;
    flex-shrink: 0;
    width: 8px;
    height: 8px;
    margin-right: 4px;
    border-radius: 50%;
    background-color: var(--color-text-third);

    &.dodger-blue {
      background-color: $dodger-blue;
    }
    &.viridian {
      background-color: $viridian;
    }
    &.algae-green {
      background-color: $algae-green;
    }
    &.bluish-grey {
      background-color: $bluish-grey;
    }
    &.bluey-purple {
      background-color: $bluey-purple;
    }
    &.strawberry {
      background-color: $strawberry;
    }
    &.darkish-pink {
      background-color: $darkish-pink;
    }
    &.reddish-orange {
      background-color: $reddish-orange;
    }
    &.light-orange {
      background-color: $light-orange;
    }
  }

  &__label {
    flex-shrink: 0;
    color: var(--color-text-third);
    margin-right: 4px;

    &::after {
      content: ':';
    }
  }

  /* Long values wrap within the pill rather than being cut off */
  &__text {
    min-width: 0;
    white-space: normal;
    word-break: break-word;
  }
}
</style>
